import {
  startOfYear,
  endOfYear,
  startOfMonth,
  endOfMonth,
  subMonths,
  subYears,
  subDays,
  differenceInCalendarDays,
  format,
  parseISO,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { totalAccountsReceivableARS } from './accountsReceivable';

export type AnalyticsPeriodPreset =
  | 'ytd'
  | 'this_month'
  | 'last_month'
  | 'last_30'
  | 'last_90'
  | 'last_year'
  | 'custom';

export interface DateRange {
  start: Date;
  end: Date;
}

export interface AnalyticsExpressCosts {
  remera_cost: number;
  buzo_cost: number;
  estampado_cost?: number;
}

const toKey = (value: string | Date | null | undefined): string => {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  return format(value, 'yyyy-MM-dd');
};

export const isInRange = (
  value: string | Date | null | undefined,
  start: Date,
  end: Date
): boolean => {
  const key = toKey(value);
  if (!key) return false;
  return key >= format(start, 'yyyy-MM-dd') && key <= format(end, 'yyyy-MM-dd');
};

export const entityActivityDate = (entity: any): string => {
  return (
    entity?.start_date ||
    entity?.date ||
    entity?.created_at ||
    ''
  );
};

export function getPeriodRange(
  preset: AnalyticsPeriodPreset,
  customStart?: string,
  customEnd?: string,
  now: Date = new Date()
): DateRange {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case 'this_month':
      return { start: startOfMonth(today), end: endOfMonth(today) };
    case 'last_month': {
      const prev = subMonths(today, 1);
      return { start: startOfMonth(prev), end: endOfMonth(prev) };
    }
    case 'last_30':
      return { start: subDays(today, 29), end: today };
    case 'last_90':
      return { start: subDays(today, 89), end: today };
    case 'last_year': {
      const prevYear = subYears(today, 1);
      return { start: startOfYear(prevYear), end: endOfYear(prevYear) };
    }
    case 'custom': {
      if (customStart && customEnd) {
        return {
          start: parseISO(customStart),
          end: parseISO(customEnd),
        };
      }
      return { start: startOfYear(today), end: today };
    }
    case 'ytd':
    default:
      return { start: startOfYear(today), end: today };
  }
}

/** Período comparable anterior de igual duración. */
export function getComparablePreviousRange(range: DateRange): DateRange {
  const days = Math.max(1, differenceInCalendarDays(range.end, range.start) + 1);
  const prevEnd = subDays(range.start, 1);
  const prevStart = subDays(prevEnd, days - 1);
  return { start: prevStart, end: prevEnd };
}

export function formatPeriodLabel(range: DateRange, preset: AnalyticsPeriodPreset): string {
  if (preset === 'ytd') return `Año corriente · ${format(range.start, 'yyyy')}`;
  if (preset === 'this_month') return format(range.start, 'MMMM yyyy', { locale: es });
  if (preset === 'last_month') return format(range.start, 'MMMM yyyy', { locale: es });
  if (preset === 'last_year') return `Año ${format(range.start, 'yyyy')}`;
  return `${format(range.start, 'd MMM yyyy', { locale: es })} – ${format(range.end, 'd MMM yyyy', { locale: es })}`;
}

export function pctChange(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

export function ppChange(current: number, previous: number): number {
  return current - previous;
}

export function formatCompactARS(amount: number): string {
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';
  if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(1).replace('.', ',')}B`;
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1).replace('.', ',')}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${Math.round(abs).toLocaleString('es-AR')}`;
}

export function formatPct(value: number, digits = 1): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(digits).replace('.', ',')}%`;
}

export function formatPp(value: number, digits = 1): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(digits).replace('.', ',')} pp`;
}

const getExpressAmount = (o: any) => Number(o.express_value ?? o.total ?? 0) || 0;
const getExpressSampleAmount = (s: any) => Number(s.sample_value ?? s.total ?? 0) || 0;
const getProductionValue = (p: any) => Number(p.production_value ?? 0) || 0;
const getSampleValue = (s: any) => Number(s.sample_value ?? 0) || 0;

const getPaymentAmountInARS = (payment: any) => {
  let amount = payment.amount || 0;
  if (payment.currency === 'USD' && payment.exchange_rate) {
    amount = amount * payment.exchange_rate;
  }
  return amount;
};

const getInvoiceAmountWithoutVAT = (invoice: any) => {
  let amount = invoice.type === 'A' ? (invoice.subtotal || 0) : (invoice.total || 0);
  if (invoice.currency === 'USD' && invoice.exchange_rate) {
    amount = amount * invoice.exchange_rate;
  }
  return amount;
};

const invoiceBalanceARS = (inv: any): number => {
  const bal = Number(inv.balance ?? 0);
  if (bal <= 0) return 0;
  if (inv.currency === 'USD' && inv.exchange_rate) return bal * inv.exchange_rate;
  return bal;
};

function countExpressUnits(
  items: any[],
  idField: string,
  entityId: string
): { remmeras: number; buzos: number } {
  const rows = items.filter((i) => i[idField] === entityId);
  let remmeras = 0;
  let buzos = 0;
  rows.forEach((item) => {
    const type = String(item.product_type || '').toLowerCase();
    const qty = Number(item.quantity || 0);
    if (type.includes('buzo') || type.includes('hoodie')) buzos += qty;
    else remmeras += qty;
  });
  return { remmeras, buzos };
}

function expressOrderCost(
  entityId: string,
  items: any[],
  idField: string,
  costs: AnalyticsExpressCosts
): number {
  const { remmeras, buzos } = countExpressUnits(items, idField, entityId);
  return remmeras * (costs.remera_cost || 0) + buzos * (costs.buzo_cost || 0);
}

export interface AnalyticsInput {
  productions: any[];
  samples: any[];
  express: any[];
  expressSamples: any[];
  productionTasks: any[];
  sampleTasks: any[];
  expressItems: any[];
  expressSampleItems: any[];
  invoices: any[];
  payments: any[];
  clients: any[];
  suppliers: any[];
  expressCosts: AnalyticsExpressCosts;
  range: DateRange;
}

export interface ClientMetricRow {
  clientId: string;
  clientName: string;
  sales: number;
  costs: number;
  margin: number;
  marginPct: number;
}

export interface OrderMarginRow {
  id: string;
  name: string;
  clientName: string;
  type: 'production' | 'sample' | 'express' | 'express_sample';
  sales: number;
  costs: number;
  margin: number;
  marginPct: number;
}

export interface SupplierCostRow {
  supplierId: string;
  supplierName: string;
  cost: number;
}

export interface AgingBucket {
  key: 'current' | '1_7' | '8_15' | '16_30' | '30_plus';
  label: string;
  amount: number;
  count: number;
}

export interface AnalyticsSnapshot {
  sales: number;
  costs: number;
  grossMargin: number;
  grossMarginPct: number;
  collected: number;
  receivable: number;
  ticketAvg: number;
  activeClients: number;
  orderCount: number;
  invoiceSales: number;
  salesSeries: { label: string; sales: number; margin: number; marginPct: number; collected: number }[];
  topClientsBySales: ClientMetricRow[];
  topClientsByMargin: ClientMetricRow[];
  clientsByProductionMargin: ClientMetricRow[];
  concentration: { top1: number; top3: number; top5: number };
  clientMix: { new: number; recurring: number; inactive: number };
  aging: AgingBucket[];
  overdueReceivable: number;
  lowMarginOrders: OrderMarginRow[];
  costsBySupplier: SupplierCostRow[];
  marginByProduction: OrderMarginRow[];
  costsByClient: ClientMetricRow[];
}

function filterEntities(list: any[], range: DateRange) {
  return list.filter((e) => isInRange(entityActivityDate(e), range.start, range.end));
}

function buildClientNameMap(clients: any[]): Record<string, string> {
  const map: Record<string, string> = {};
  clients.forEach((c) => {
    map[c.id] = c.name || 'Sin nombre';
  });
  return map;
}

function taskCostsByParent(
  tasks: any[],
  parentIdField: 'production_id' | 'sample_id',
  parents: any[],
  range: DateRange
): Map<string, number> {
  const parentIds = new Set(
    parents.filter((p) => isInRange(entityActivityDate(p), range.start, range.end)).map((p) => p.id)
  );
  const map = new Map<string, number>();
  tasks.forEach((t) => {
    const pid = t[parentIdField];
    if (!pid || !parentIds.has(pid)) return;
    map.set(pid, (map.get(pid) || 0) + (Number(t.cost) || 0));
  });
  return map;
}

export function computeAnalyticsSnapshot(input: AnalyticsInput): AnalyticsSnapshot {
  const {
    productions,
    samples,
    express,
    expressSamples,
    productionTasks,
    sampleTasks,
    expressItems,
    expressSampleItems,
    invoices,
    payments,
    clients,
    suppliers,
    expressCosts,
    range,
  } = input;

  const names = buildClientNameMap(clients);
  const periodProductions = filterEntities(productions, range);
  const periodSamples = filterEntities(samples, range);
  const periodExpress = filterEntities(express, range);
  const periodExpressSamples = filterEntities(expressSamples, range);

  const prodCosts = taskCostsByParent(productionTasks, 'production_id', productions, range);
  const sampleCosts = taskCostsByParent(sampleTasks, 'sample_id', samples, range);

  // Ventas = facturación a clientes (misma lógica que Dashboard)
  const periodInvoices = invoices.filter(
    (inv) =>
      inv.entity_type === 'client' &&
      !inv.voided &&
      isInRange(inv.date, range.start, range.end)
  );
  const sales = periodInvoices.reduce((sum, inv) => sum + getInvoiceAmountWithoutVAT(inv), 0);
  const invoiceSales = sales;
  const invoiceCount = periodInvoices.length;

  // Margen = valor de producciones/muestras (y express) del período − costos directos
  let productionValue = 0;
  let costs = 0;
  const clientMarginAgg = new Map<string, { value: number; costs: number }>();
  const clientProductionMarginAgg = new Map<string, { value: number; costs: number }>();
  const clientBillingAgg = new Map<string, number>();
  const ensureMarginClient = (id: string) => {
    if (!clientMarginAgg.has(id)) clientMarginAgg.set(id, { value: 0, costs: 0 });
    return clientMarginAgg.get(id)!;
  };
  const ensureProductionMarginClient = (id: string) => {
    if (!clientProductionMarginAgg.has(id)) clientProductionMarginAgg.set(id, { value: 0, costs: 0 });
    return clientProductionMarginAgg.get(id)!;
  };

  periodInvoices.forEach((inv) => {
    if (!inv.entity_id) return;
    clientBillingAgg.set(
      inv.entity_id,
      (clientBillingAgg.get(inv.entity_id) || 0) + getInvoiceAmountWithoutVAT(inv)
    );
  });

  const orderRows: OrderMarginRow[] = [];

  periodProductions.forEach((p) => {
    const val = getProductionValue(p);
    const c = prodCosts.get(p.id) || 0;
    productionValue += val;
    costs += c;
    if (p.client_id) {
      const row = ensureMarginClient(p.client_id);
      row.value += val;
      row.costs += c;
      const prodRow = ensureProductionMarginClient(p.client_id);
      prodRow.value += val;
      prodRow.costs += c;
    }
    orderRows.push({
      id: p.id,
      name: p.name || 'Producción',
      clientName: names[p.client_id] || '—',
      type: 'production',
      sales: val,
      costs: c,
      margin: val - c,
      marginPct: val > 0 ? ((val - c) / val) * 100 : 0,
    });
  });

  periodSamples.forEach((s) => {
    const val = getSampleValue(s);
    const c = sampleCosts.get(s.id) || 0;
    productionValue += val;
    costs += c;
    if (s.client_id) {
      const row = ensureMarginClient(s.client_id);
      row.value += val;
      row.costs += c;
    }
    orderRows.push({
      id: s.id,
      name: s.name || 'Muestra',
      clientName: names[s.client_id] || '—',
      type: 'sample',
      sales: val,
      costs: c,
      margin: val - c,
      marginPct: val > 0 ? ((val - c) / val) * 100 : 0,
    });
  });

  periodExpress.forEach((o) => {
    const val = getExpressAmount(o);
    const c = expressOrderCost(o.id, expressItems, 'express_id', expressCosts);
    productionValue += val;
    costs += c;
    if (o.client_id) {
      const row = ensureMarginClient(o.client_id);
      row.value += val;
      row.costs += c;
    }
    orderRows.push({
      id: o.id,
      name: o.name || 'Express',
      clientName: names[o.client_id] || '—',
      type: 'express',
      sales: val,
      costs: c,
      margin: val - c,
      marginPct: val > 0 ? ((val - c) / val) * 100 : 0,
    });
  });

  periodExpressSamples.forEach((o) => {
    const val = getExpressSampleAmount(o);
    const c = expressOrderCost(o.id, expressSampleItems, 'express_sample_id', expressCosts);
    productionValue += val;
    costs += c;
    if (o.client_id) {
      const row = ensureMarginClient(o.client_id);
      row.value += val;
      row.costs += c;
    }
    orderRows.push({
      id: o.id,
      name: o.name || 'Muestra Express',
      clientName: names[o.client_id] || '—',
      type: 'express_sample',
      sales: val,
      costs: c,
      margin: val - c,
      marginPct: val > 0 ? ((val - c) / val) * 100 : 0,
    });
  });

  const grossMargin = productionValue - costs;
  const grossMarginPct = productionValue > 0 ? (grossMargin / productionValue) * 100 : 0;
  const orderCount =
    periodProductions.length +
    periodSamples.length +
    periodExpress.length +
    periodExpressSamples.length;
  const ticketAvg = invoiceCount > 0 ? sales / invoiceCount : 0;

  const activeClientIds = new Set<string>();
  [...periodProductions, ...periodSamples, ...periodExpress, ...periodExpressSamples].forEach((e) => {
    if (e.client_id) activeClientIds.add(e.client_id);
  });
  periodInvoices.forEach((inv) => {
    if (inv.entity_id) activeClientIds.add(inv.entity_id);
  });

  const collected = payments
    .filter((p) => !p.voided && isInRange(p.date, range.start, range.end))
    .filter((p) => {
      if (!p.invoice_id) return true;
      const inv = invoices.find((i) => i.id === p.invoice_id);
      return !inv || inv.entity_type === 'client';
    })
    .reduce((sum, p) => sum + getPaymentAmountInARS(p), 0);

  const receivable = totalAccountsReceivableARS(clients, invoices, payments);

  // Series: ventas = facturas; margen = valor prod/muestra − costos
  const series: AnalyticsSnapshot['salesSeries'] = [];
  let cursor = startOfMonth(range.start);
  const endMonth = startOfMonth(range.end);
  while (cursor <= endMonth) {
    const mStart = startOfMonth(cursor);
    const mEndRaw = endOfMonth(cursor);
    const mEnd = mEndRaw > range.end ? range.end : mEndRaw;
    const mRange = { start: mStart > range.start ? mStart : range.start, end: mEnd };

    const mSales = invoices
      .filter(
        (inv) =>
          inv.entity_type === 'client' &&
          !inv.voided &&
          isInRange(inv.date, mRange.start, mRange.end)
      )
      .reduce((sum, inv) => sum + getInvoiceAmountWithoutVAT(inv), 0);

    const mProdValue =
      filterEntities(productions, mRange).reduce((s, p) => s + getProductionValue(p), 0) +
      filterEntities(samples, mRange).reduce((s, p) => s + getSampleValue(p), 0) +
      filterEntities(express, mRange).reduce((s, p) => s + getExpressAmount(p), 0) +
      filterEntities(expressSamples, mRange).reduce((s, p) => s + getExpressSampleAmount(p), 0);

    const mProdCosts = taskCostsByParent(productionTasks, 'production_id', productions, mRange);
    const mSampleCosts = taskCostsByParent(sampleTasks, 'sample_id', samples, mRange);
    let mCosts = 0;
    filterEntities(productions, mRange).forEach((p) => {
      mCosts += mProdCosts.get(p.id) || 0;
    });
    filterEntities(samples, mRange).forEach((p) => {
      mCosts += mSampleCosts.get(p.id) || 0;
    });
    filterEntities(express, mRange).forEach((o) => {
      mCosts += expressOrderCost(o.id, expressItems, 'express_id', expressCosts);
    });
    filterEntities(expressSamples, mRange).forEach((o) => {
      mCosts += expressOrderCost(o.id, expressSampleItems, 'express_sample_id', expressCosts);
    });

    const mCollected = payments
      .filter((p) => !p.voided && isInRange(p.date, mRange.start, mRange.end))
      .filter((p) => {
        if (!p.invoice_id) return true;
        const inv = invoices.find((i) => i.id === p.invoice_id);
        return !inv || inv.entity_type === 'client';
      })
      .reduce((sum, p) => sum + getPaymentAmountInARS(p), 0);

    const mMargin = mProdValue - mCosts;
    series.push({
      label: format(mStart, 'MMM yy', { locale: es }),
      sales: mSales,
      margin: mMargin,
      marginPct: mProdValue > 0 ? (mMargin / mProdValue) * 100 : 0,
      collected: mCollected,
    });

    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }

  const billingRows: ClientMetricRow[] = Array.from(clientBillingAgg.entries()).map(
    ([clientId, billing]) => ({
      clientId,
      clientName: names[clientId] || '—',
      sales: billing,
      costs: 0,
      margin: 0,
      marginPct: 0,
    })
  );

  const marginRows: ClientMetricRow[] = Array.from(clientMarginAgg.entries()).map(
    ([clientId, v]) => ({
      clientId,
      clientName: names[clientId] || '—',
      sales: v.value,
      costs: v.costs,
      margin: v.value - v.costs,
      marginPct: v.value > 0 ? ((v.value - v.costs) / v.value) * 100 : 0,
    })
  );

  const topClientsBySales = [...billingRows].sort((a, b) => b.sales - a.sales).slice(0, 8);
  const topClientsByMargin = [...marginRows].sort((a, b) => b.margin - a.margin).slice(0, 8);
  const clientsByProductionMargin = Array.from(clientProductionMarginAgg.entries())
    .map(([clientId, v]) => ({
      clientId,
      clientName: names[clientId] || '—',
      sales: v.value,
      costs: v.costs,
      margin: v.value - v.costs,
      marginPct: v.value > 0 ? ((v.value - v.costs) / v.value) * 100 : 0,
    }))
    .sort((a, b) => b.margin - a.margin)
    .slice(0, 8);

  const sortedSales = [...billingRows].sort((a, b) => b.sales - a.sales);
  const totalClientSales = sortedSales.reduce((s, c) => s + c.sales, 0) || 1;
  const concentration = {
    top1: (sortedSales.slice(0, 1).reduce((s, c) => s + c.sales, 0) / totalClientSales) * 100,
    top3: (sortedSales.slice(0, 3).reduce((s, c) => s + c.sales, 0) / totalClientSales) * 100,
    top5: (sortedSales.slice(0, 5).reduce((s, c) => s + c.sales, 0) / totalClientSales) * 100,
  };

  // Client mix
  const lookbackStart = subYears(range.start, 2);
  const hadActivityBefore = new Set<string>();
  const allBefore = [...productions, ...samples, ...express, ...expressSamples];
  allBefore.forEach((e) => {
    if (!e.client_id) return;
    const d = entityActivityDate(e);
    if (d && toKey(d) < format(range.start, 'yyyy-MM-dd') && toKey(d) >= format(lookbackStart, 'yyyy-MM-dd')) {
      hadActivityBefore.add(e.client_id);
    }
  });
  invoices
    .filter((inv) => inv.entity_type === 'client' && !inv.voided && inv.entity_id)
    .forEach((inv) => {
      const key = toKey(inv.date);
      if (key && key < format(range.start, 'yyyy-MM-dd')) hadActivityBefore.add(inv.entity_id);
    });

  let newCount = 0;
  let recurringCount = 0;
  activeClientIds.forEach((id) => {
    if (hadActivityBefore.has(id)) recurringCount += 1;
    else newCount += 1;
  });

  const recentlyActive = new Set<string>();
  const yearAgo = subYears(range.start, 1);
  allBefore.forEach((e) => {
    if (!e.client_id) return;
    if (isInRange(entityActivityDate(e), yearAgo, subDays(range.start, 1))) {
      recentlyActive.add(e.client_id);
    }
  });
  let inactiveCount = 0;
  recentlyActive.forEach((id) => {
    if (!activeClientIds.has(id)) inactiveCount += 1;
  });

  // Aging
  const today = new Date();
  const agingMap: Record<AgingBucket['key'], AgingBucket> = {
    current: { key: 'current', label: 'No vencido', amount: 0, count: 0 },
    '1_7': { key: '1_7', label: '1–7 días', amount: 0, count: 0 },
    '8_15': { key: '8_15', label: '8–15 días', amount: 0, count: 0 },
    '16_30': { key: '16_30', label: '16–30 días', amount: 0, count: 0 },
    '30_plus': { key: '30_plus', label: '+30 días', amount: 0, count: 0 },
  };

  let overdueReceivable = 0;
  invoices
    .filter((inv) => inv.entity_type === 'client' && !inv.voided && invoiceBalanceARS(inv) > 0)
    .forEach((inv) => {
      const amount = invoiceBalanceARS(inv);
      const due = inv.due_date ? parseISO(toKey(inv.due_date)) : parseISO(toKey(inv.date));
      const daysOverdue = differenceInCalendarDays(today, due);
      let key: AgingBucket['key'] = 'current';
      if (daysOverdue >= 1 && daysOverdue <= 7) key = '1_7';
      else if (daysOverdue >= 8 && daysOverdue <= 15) key = '8_15';
      else if (daysOverdue >= 16 && daysOverdue <= 30) key = '16_30';
      else if (daysOverdue > 30) key = '30_plus';
      agingMap[key].amount += amount;
      agingMap[key].count += 1;
      if (daysOverdue >= 1) overdueReceivable += amount;
    });

  const aging = Object.values(agingMap);

  const lowMarginOrders = [...orderRows]
    .filter((o) => o.sales > 0 && o.marginPct >= -200)
    .sort((a, b) => a.marginPct - b.marginPct)
    .slice(0, 8);

  const supplierMap = new Map<string, number>();
  productionTasks.forEach((t) => {
    const prod = productions.find((p) => p.id === t.production_id);
    if (!prod || !isInRange(entityActivityDate(prod), range.start, range.end)) return;
    if (!t.supplier_id) return;
    supplierMap.set(t.supplier_id, (supplierMap.get(t.supplier_id) || 0) + (Number(t.cost) || 0));
  });
  sampleTasks.forEach((t) => {
    const sample = samples.find((s) => s.id === t.sample_id);
    if (!sample || !isInRange(entityActivityDate(sample), range.start, range.end)) return;
    if (!t.supplier_id) return;
    supplierMap.set(t.supplier_id, (supplierMap.get(t.supplier_id) || 0) + (Number(t.cost) || 0));
  });

  const supplierNames: Record<string, string> = {};
  suppliers.forEach((s) => {
    supplierNames[s.id] = s.name || 'Proveedor';
  });

  const costsBySupplier: SupplierCostRow[] = Array.from(supplierMap.entries())
    .map(([supplierId, cost]) => ({
      supplierId,
      supplierName: supplierNames[supplierId] || '—',
      cost,
    }))
    .sort((a, b) => b.cost - a.cost)
    .slice(0, 8);

  const marginByProduction = orderRows
    .filter((o) => o.type === 'production' && o.sales > 0)
    .sort((a, b) => b.margin - a.margin)
    .slice(0, 8);

  const costsByClient = [...marginRows].sort((a, b) => b.costs - a.costs).slice(0, 8);

  return {
    sales,
    costs,
    grossMargin,
    grossMarginPct,
    collected,
    receivable,
    ticketAvg,
    activeClients: activeClientIds.size,
    orderCount,
    invoiceSales,
    salesSeries: series,
    topClientsBySales,
    topClientsByMargin,
    clientsByProductionMargin,
    concentration,
    clientMix: { new: newCount, recurring: recurringCount, inactive: inactiveCount },
    aging,
    overdueReceivable,
    lowMarginOrders,
    costsBySupplier,
    marginByProduction,
    costsByClient,
  };
}

export interface AnalyticsInsight {
  id: string;
  tone: 'positive' | 'negative' | 'neutral' | 'warning';
  text: string;
}

export function buildInsights(
  current: AnalyticsSnapshot,
  previous: AnalyticsSnapshot
): AnalyticsInsight[] {
  const insights: AnalyticsInsight[] = [];
  const salesDelta = pctChange(current.sales, previous.sales);
  if (salesDelta !== null) {
    insights.push({
      id: 'sales',
      tone: salesDelta >= 0 ? 'positive' : 'negative',
      text: `Ventas ${formatPct(salesDelta)} vs período anterior`,
    });
  }

  const marginPp = ppChange(current.grossMarginPct, previous.grossMarginPct);
  if (Number.isFinite(marginPp)) {
    insights.push({
      id: 'margin-pp',
      tone: marginPp >= 0 ? 'positive' : 'negative',
      text: `Margen ${marginPp >= 0 ? 'subió' : 'cayó'} ${Math.abs(marginPp).toFixed(1).replace('.', ',')} pp`,
    });
  }

  insights.push({
    id: 'concentration',
    tone: current.concentration.top3 >= 60 ? 'warning' : 'neutral',
    text: `El Top 3 de clientes representa ${current.concentration.top3.toFixed(0)}% de las ventas`,
  });

  if (current.overdueReceivable > 0) {
    insights.push({
      id: 'overdue',
      tone: 'warning',
      text: `${formatCompactARS(current.overdueReceivable)} de cobranzas están vencidas`,
    });
  }

  const collectedDelta = pctChange(current.collected, previous.collected);
  if (collectedDelta !== null) {
    insights.push({
      id: 'collected',
      tone: collectedDelta >= 0 ? 'positive' : 'negative',
      text: `Cobranzas ${formatPct(collectedDelta)} vs período anterior`,
    });
  }

  return insights.slice(0, 4);
}
